/**
 * CadenceCalendar.jsx — week-view calendar for the cold-outreach follow-up
 * cadence. Default view on the Sync BigDough page (replaces the old flat
 * upload-history list, which is still available via a toggle).
 *
 * Each date column shows every list's stage that's either due that day
 * (scheduled, not sent yet) or was actually sent that day (historical) —
 * both render together so the view works as a single day-by-day picture.
 * Color = stage number (First Send, Follow-up 1-4); solid = sent, dashed
 * outline = scheduled. Clicking an entry opens the content panel: view/edit
 * the per-stage draft copy, copy it, jump straight to the upload form, or
 * manually mark it sent/not sent (for a send that happened but isn't
 * reflected in bd_uploads yet — see cadence_manual_status in
 * backend/migrations/0001_initial-schema.sql). Each day also has a one-click "mark day sent" to
 * bulk-log every not-yet-sent entry shown that day.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchCadence, saveCadenceContent, setCadenceStageSent, setCadenceUploadStage } from '../api/backend.js';

// 4 stages: First Send, Follow-up 1/2/3 (see backend/cadence.js STAGE_OFFSETS).
const STAGE_COLORS = [
  { bg: 'var(--purple-bg, #EEEDFE)', border: '#7F77DD', text: '#3C3489' },
  { bg: 'var(--blue-bg, #E6F1FB)',   border: '#378ADD', text: '#0C447C' },
  { bg: 'var(--teal-bg, #E1F5EE)',   border: '#1D9E75', text: '#085041' },
  { bg: 'var(--amber-bg, #FAEEDA)',  border: '#BA7517', text: '#633806' },
];
const MAX_VISIBLE_PER_DAY = 3;

function dateKey(d) {
  return new Date(d).toISOString().slice(0, 10);
}
function startOfWeek(d) {
  // Anchor off the LOCAL calendar date (what the user considers "today"),
  // not the UTC one — using getUTCFullYear/Month/Date on a local Date here
  // was the bug: near midnight UTC, "today" locally and "today" in UTC can
  // be different dates, silently shifting the whole displayed week by one
  // day depending on the viewer's timezone.
  const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = dt.getUTCDay(); // 0 = Sunday
  const diff = day === 0 ? -6 : 1 - day; // days back to Monday; Sunday is a special case (6 days back, not forward)
  dt.setUTCDate(dt.getUTCDate() + diff);
  return dt;
}
function addDays(d, n) {
  const dt = new Date(d);
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt;
}
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function CadenceCalendar({ getToken, onPrefillUpload, onViewInList }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timelines, setTimelines] = useState([]);
  const [specRotation, setSpecRotation] = useState(null);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [expandedDays, setExpandedDays] = useState({});
  const [selected, setSelected] = useState(null); // { listName, stage, entry }
  const [draft, setDraft] = useState({ subject: '', body: '' });
  const [saving, setSaving] = useState(false);
  const [copyLabel, setCopyLabel] = useState('Copy content');
  const [markingDay, setMarkingDay] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchCadence(getToken);
      setTimelines(res.timelines ?? []);
      setSpecRotation(res.specRotation ?? null);
    } catch (e) {
      setError(e.message || 'Failed to load cadence data');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { load(); }, [load]);

  // date (YYYY-MM-DD) -> array of { listName, stage, label, dateVal, sent, content }
  const entriesByDate = useMemo(() => {
    const map = {};
    for (const t of timelines) {
      for (const s of t.stages) {
        const sent = s.status === 'sent';
        const dateVal = sent ? s.sentAt : s.dueDate;
        if (!dateVal) continue;
        if (!sent && (s.status === 'not_started')) continue;
        const key = dateKey(dateVal);
        (map[key] ??= []).push({ listName: t.listName, stage: s.stage, label: s.label, dateVal, sent, status: s.status, content: s.content, opens: s.opens, recipientCount: s.recipientCount, openRate: s.openRate, uploadId: s.uploadId, manualOverride: !!s.manualOverride });
      }
    }
    // Spec rotation — a separate shared queue (see backend/specRotation.js),
    // not a per-list stage. No content to view/edit, so these render as
    // plain non-clickable chips rather than going through openEntry.
    for (const q of specRotation?.queue ?? []) {
      const dateVal = q.sentAt ?? q.dueDate;
      if (!dateVal) continue;
      const key = dateKey(dateVal);
      (map[key] ??= []).push({ listName: 'Spec', stage: null, label: q.industry, dateVal, sent: q.status === 'sent', status: q.status, isSpec: true });
    }
    return map;
  }, [timelines, specRotation]);

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const weekLabel = `${weekDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

  // The stage right before the one currently open, if it was actually sent —
  // groups (First Send/Follow-up 1/2/3) are already positional in the same
  // timeline object, so no cross-list matching needed here (unlike the flat
  // Upload History list, which has to fold list_name variants itself).
  const previousStage = useMemo(() => {
    if (!selected || selected.stage == null || selected.stage <= 0 || selected.isSpec) return null;
    const t = timelines.find((tl) => tl.listName === selected.listName);
    const prev = t?.stages?.[selected.stage - 1];
    return prev?.sentAt ? prev : null;
  }, [selected, timelines]);

  // Every stage label for the selected entry's list, for the "reassign
  // stage" dropdown — a real send can be corrected to any of this list's
  // tracked stages, not just adjacent ones.
  const selectedListStages = useMemo(() => {
    if (!selected || selected.isSpec) return [];
    return timelines.find((tl) => tl.listName === selected.listName)?.stages ?? [];
  }, [selected, timelines]);

  function openEntry(entry) {
    setSelected(entry);
    setDraft({ subject: entry.content?.subject ?? '', body: entry.content?.body ?? '' });
    setCopyLabel('Copy content');
  }

  async function handleSaveDraft() {
    if (!selected) return;
    setSaving(true);
    try {
      await saveCadenceContent(getToken, selected.listName, selected.stage, draft);
      await load();
    } catch (e) {
      setError(e.message || 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  }

  function handleCopy() {
    const text = `${draft.subject}\n\n${draft.body}`;
    navigator.clipboard?.writeText(text).then(() => {
      setCopyLabel('Copied');
      setTimeout(() => setCopyLabel('Copy content'), 1500);
    }).catch(() => {});
  }

  function handleGoToUpload() {
    if (!selected) return;
    onPrefillUpload?.({ listName: selected.listName, subject: draft.subject, body: draft.body });
    setSelected(null);
  }

  // Corrects which stage a real send counts as — see cadence_stage_override
  // in backend/migrations/0001_initial-schema.sql. For when the automatic date-order inference
  // gets it wrong (a skipped or out-of-order send). Only meaningful for a
  // real bd_uploads-backed send (selected.uploadId) — a manual sent/unsent
  // mark has no upload to reassign.
  async function handleReassignStage(newStage) {
    if (!selected?.uploadId || newStage === selected.stage) return;
    setSaving(true);
    try {
      await setCadenceUploadStage(getToken, selected.uploadId, selected.listName, newStage);
      await load();
      setSelected(null);
    } catch (e) {
      setError(e.message || 'Failed to reassign stage');
    } finally {
      setSaving(false);
    }
  }

  // Manual sent/not-sent toggle for one stage — see cadence_manual_status in
  // backend/migrations/0001_initial-schema.sql. Only a manually-marked entry can be un-marked; a
  // real bd_uploads-backed send isn't something to "unsend" from here.
  async function handleToggleSent(nextSent) {
    if (!selected || selected.stage == null || selected.isSpec) return;
    setSaving(true);
    try {
      await setCadenceStageSent(getToken, selected.listName, selected.stage, nextSent, nextSent ? selected.dateVal : undefined);
      await load();
      setSelected(null);
    } catch (e) {
      setError(e.message || 'Failed to update sent status');
    } finally {
      setSaving(false);
    }
  }

  // Bulk-log every not-yet-sent entry for one calendar day as sent in one
  // click — for days where several lists' emails all actually went out
  // together but bd_uploads didn't pick all of them up.
  async function handleMarkDaySent(dayKeyStr, dayEntries) {
    const targets = dayEntries.filter((e) => !e.isSpec && !e.sent);
    if (!targets.length) return;
    setMarkingDay(dayKeyStr);
    try {
      for (const e of targets) {
        await setCadenceStageSent(getToken, e.listName, e.stage, true, e.dateVal);
      }
      await load();
    } catch (e) {
      setError(e.message || 'Failed to mark day sent');
    } finally {
      setMarkingDay(null);
    }
  }

  if (loading) return <div style={{ padding: 20, color: 'var(--text-3)', fontSize: 13 }}>Loading calendar…</div>;
  if (error) return <div style={{ padding: 20, color: 'var(--red, #dc2626)', fontSize: 13 }}>⚠ {error}</div>;

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 16, fontSize: 11.5, color: 'var(--text-3)' }}>
          {STAGE_COLORS.map((c, i) => (
            <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 9, height: 9, borderRadius: 2, background: c.border, display: 'inline-block' }} />
              {i === 0 ? 'First send' : `Follow-up ${i}`}
            </span>
          ))}
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: '#64748b', display: 'inline-block' }} />
            Spec rotation
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="btn btn--ghost btn--sm" onClick={() => setWeekStart((w) => addDays(w, -7))}>‹</button>
          <span style={{ fontSize: 12.5, color: 'var(--text-2)', minWidth: 110, textAlign: 'center' }}>{weekLabel}</span>
          <button className="btn btn--ghost btn--sm" onClick={() => setWeekStart((w) => addDays(w, 7))}>›</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0,1fr))', gap: 8 }}>
        {weekDays.map((d, i) => {
          const key = dateKey(d);
          const entries = (entriesByDate[key] ?? []).sort((a, b) => (a.stage ?? 99) - (b.stage ?? 99));
          const isWeekend = d.getUTCDay() === 6 || d.getUTCDay() === 0;
          const expanded = !!expandedDays[key];
          const visible = expanded ? entries : entries.slice(0, MAX_VISIBLE_PER_DAY);
          const hiddenCount = entries.length - visible.length;
          return (
            <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 6, minHeight: 90 }}>
              <div style={{ fontSize: 11, color: 'var(--text-3)', textAlign: 'center' }}>
                {DAY_LABELS[i]}
                <div style={{ fontSize: 14, fontWeight: 600, color: isWeekend ? 'var(--text-3)' : 'var(--navy)' }}>{d.getUTCDate()}</div>
              </div>
              {visible.map((entry) => {
                const c = entry.isSpec
                  ? { bg: '#f1f5f9', border: '#64748b', text: '#334155' }
                  : (STAGE_COLORS[entry.stage] ?? STAGE_COLORS[0]);
                return (
                  <div
                    key={entry.isSpec ? `spec-${entry.label}` : `${entry.listName}-${entry.stage}`}
                    onClick={entry.isSpec ? undefined : () => openEntry(entry)}
                    style={{
                      cursor: entry.isSpec ? 'default' : 'pointer', borderRadius: 6, padding: '4px 6px',
                      background: c.bg, color: c.text,
                      border: entry.sent ? `1px solid ${c.border}` : `1.5px dashed ${c.border}`,
                    }}
                  >
                    <div style={{ fontSize: 10.5, fontWeight: 700 }}>{entry.listName} · {entry.label}</div>
                    <div style={{ fontSize: 9.5 }}>{entry.sent ? 'Sent' : entry.status === 'overdue' ? 'Overdue' : entry.status === 'due_soon' ? 'Due soon' : 'Upcoming'}</div>
                  </div>
                );
              })}
              {hiddenCount > 0 && (
                <button
                  onClick={() => setExpandedDays((p) => ({ ...p, [key]: true }))}
                  style={{ fontSize: 10, color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}
                >
                  +{hiddenCount} more
                </button>
              )}
              {entries.some((e) => !e.isSpec && !e.sent) && (
                <button
                  onClick={() => handleMarkDaySent(key, entries)}
                  disabled={markingDay === key}
                  title="Mark every not-yet-sent email listed this day as sent"
                  style={{ fontSize: 9.5, color: 'var(--purple, #7c3aed)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', textAlign: 'center' }}
                >
                  {markingDay === key ? 'Marking…' : '✓ Mark day sent'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {selected && (
        <div
          onClick={() => setSelected(null)}
          style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--surface)', borderRadius: 10, border: '1px solid var(--border)', padding: '18px 20px', width: 380 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--purple, #7c3aed)', textTransform: 'uppercase', letterSpacing: '.4px' }}>📋 {selected.listName}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy)' }}>{selected.label}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                  {selected.sent ? `Sent ${new Date(selected.dateVal).toLocaleDateString()}` : `Due ${new Date(selected.dateVal).toLocaleDateString()}`}
                  {selected.sent && selected.openRate != null && ` · ${Math.round(selected.openRate * 100)}% open rate`}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                  {previousStage && (
                    <button
                      onClick={() => { setWeekStart(startOfWeek(new Date(previousStage.sentAt))); setSelected(null); }}
                      className="btn btn--ghost btn--sm"
                      style={{ fontSize: 10.5, padding: '2px 8px', height: 'auto', color: 'var(--text-2)' }}
                      title="Jump to that week"
                    >
                      ↑ Previous send ({previousStage.label}): {new Date(previousStage.sentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · {previousStage.recipientCount ?? 0} delivered
                    </button>
                  )}
                  {selected.sent && selected.uploadId && onViewInList && (
                    <button
                      onClick={() => { onViewInList(selected.uploadId); setSelected(null); }}
                      className="btn btn--ghost btn--sm"
                      style={{ fontSize: 10.5, padding: '2px 8px', height: 'auto', color: 'var(--purple, #7c3aed)' }}
                      title="Open this upload in the List view"
                    >
                      → View in List
                    </button>
                  )}
                </div>
              </div>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--text-3)' }}>×</button>
            </div>
            <label style={{ fontSize: 11.5, color: 'var(--text-3)' }}>Subject</label>
            <input
              type="text"
              value={draft.subject}
              onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
              disabled={selected.sent}
              style={{ width: '100%', marginBottom: 8, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }}
            />
            <label style={{ fontSize: 11.5, color: 'var(--text-3)' }}>Body</label>
            <textarea
              rows={5}
              value={draft.body}
              onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
              disabled={selected.sent}
              style={{ width: '100%', resize: 'vertical', padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, fontFamily: 'inherit' }}
            />
            {selected.sent && selected.uploadId && !selected.isSpec && selectedListStages.length > 0 && (
              <label style={{ fontSize: 11.5, color: 'var(--text-3)', display: 'block', marginTop: 8 }}>
                Wrong stage? Auto-detected as "{selected.label}" — correct it:
                <select
                  value={selected.stage}
                  onChange={(e) => handleReassignStage(Number(e.target.value))}
                  disabled={saving}
                  style={{ display: 'block', width: '100%', marginTop: 3, padding: '5px 7px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }}
                >
                  {selectedListStages.map((s) => (
                    <option key={s.stage} value={s.stage}>{s.label}</option>
                  ))}
                </select>
              </label>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="btn btn--ghost btn--sm" style={{ flex: 1 }} onClick={handleCopy}>{copyLabel}</button>
              {!selected.sent && !selected.isSpec && (
                <>
                  <button className="btn btn--ghost btn--sm" style={{ flex: 1 }} onClick={handleSaveDraft} disabled={saving}>
                    {saving ? 'Saving…' : 'Save draft'}
                  </button>
                  <button className="btn btn--primary btn--sm" style={{ flex: 1 }} onClick={() => handleToggleSent(true)} disabled={saving}>
                    {saving ? 'Marking…' : '✓ Mark as sent'}
                  </button>
                  <button className="btn btn--primary btn--sm" style={{ flex: 1 }} onClick={handleGoToUpload}>Go to upload</button>
                </>
              )}
              {selected.sent && selected.manualOverride && (
                <button className="btn btn--ghost btn--sm" style={{ flex: 1 }} onClick={() => handleToggleSent(false)} disabled={saving}>
                  {saving ? 'Updating…' : 'Undo — mark as not sent'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
